import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { QcRunResponse } from '../api.ts';
import { postRunNote } from '../api.ts';
import { Alert } from './Alert.tsx';
import styles from './ResultActions.module.css';

/** Metadata lifted to the parent after a submit so it can flip the run's posted state. */
export interface NotePostedMeta {
  noteId: string | null;
  notePostedAt?: string;
  notePostedBy?: string;
  workflowTriggered?: boolean;
}

interface ResultActionsProps {
  run: QcRunResponse;
  /** True only for the latest run — Submit is disabled (tooltip) on historical runs. */
  allowSubmit: boolean;
  onSubmitted: (meta: NotePostedMeta) => void;
  onRerun: () => void;
  onStartOver: () => void;
}

// idle → confirming → submitting → submitted, with two failure exits:
//   noteError     — the Forth note post failed; retry re-posts.
//   workflowError — note posted but the N8N workflow didn't trigger; retry is note-SAFE.
type ActionState = 'idle' | 'confirming' | 'submitting' | 'submitted' | 'noteError' | 'workflowError';

function initialState(run: QcRunResponse): ActionState {
  if (run.notePosted && run.workflowTriggered) return 'submitted';
  if (run.notePosted) return 'workflowError';
  return 'idle';
}

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12l5 5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const RerunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 12a8 8 0 1 1-2.3-5.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 4v4h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const HomeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 11l8-6.5L20 11M6.2 9.3V19h11.6V9.3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function ResultActions({ run, allowSubmit, onSubmitted, onRerun, onStartOver }: ResultActionsProps) {
  const { t } = useTranslation('ui');
  const [state, setState] = useState<ActionState>(() => initialState(run));
  const [errorText, setErrorText] = useState<string | null>(null);
  // Completion message(s) N8N returned on the last successful trigger. In-memory only —
  // not persisted, so they don't reappear on a page reload of an already-submitted run.
  const [workflowMessages, setWorkflowMessages] = useState<string[]>([]);
  const inFlight = useRef(false);

  async function submit() {
    if (inFlight.current) return;
    inFlight.current = true;
    setState('submitting');
    setErrorText(null);
    try {
      const r = await postRunNote(run.contactId, run.runId);
      if (r.posted) {
        onSubmitted({
          noteId: r.noteId ?? null,
          notePostedAt: r.notePostedAt,
          notePostedBy: r.notePostedBy,
          workflowTriggered: r.workflowTriggered ?? false,
        });
        setWorkflowMessages(r.workflowTriggered ? r.messages ?? [] : []);
        setState(r.workflowTriggered ? 'submitted' : 'workflowError');
      } else {
        setErrorText(r.error ?? t('submit.noteErrorGeneric'));
        setState('noteError');
      }
    } catch {
      setErrorText(t('submit.serverError'));
      setState('noteError');
    } finally {
      inFlight.current = false;
    }
  }

  const isRetry = state === 'workflowError' || state === 'noteError';
  const submitError = isRetry
    ? state === 'workflowError'
      ? t('submit.workflowError')
      : errorText ?? t('submit.noteErrorGeneric')
    : null;

  // Safety gate (client mirror of the server enforcement boundary). The note-post route
  // rejects these same codes for every caller — see core `SUBMIT_BLOCKING_ERROR_CODES`, the
  // source of truth — so posting a mis-enrolled file can't advance the workflow. Here we
  // disable Submit and explain why. The enrollment_plan_present check emits these codes:
  // wrong_program (plan belongs to a different program) and no_plan (no plan on file). Either
  // hard-blocks Submit (overriding retry states). Kept as literals: the UI bundle does not
  // depend on core.
  const runErrorCodes = new Set(run.results.flatMap((r) => r.errorCodes));
  const submitBlockedMessage = runErrorCodes.has('enrollment_plan_present.wrong_program')
    ? t('submit.blockedWrongProgram')
    : runErrorCodes.has('enrollment_plan_present.no_plan')
    ? t('submit.blockedNoPlan')
    : null;

  const submitDisabled =
    submitBlockedMessage !== null ||
    state === 'submitting' ||
    state === 'submitted' ||
    (!allowSubmit && !isRetry);
  const submitTitle =
    submitBlockedMessage !== null
      ? t('submit.blockedTooltip')
      : state === 'submitted' && run.notePostedAt && run.notePostedBy
      ? t('submit.submittedStamp', {
          date: new Date(run.notePostedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
          email: run.notePostedBy,
        })
      : !allowSubmit && state === 'idle'
      ? t('submit.historicalTooltip')
      : undefined;

  // The Submit slot is positionally fixed; only its label/look/handler change by state.
  // A clean pass submits directly; anything else routes to the QC team's manual review.
  let submitLabel = run.overallStatus === 'PASS' ? t('submit.submit') : t('submit.submitForReview');
  let submitIcon = <CheckIcon />;
  let submitClass = `${styles.btn} ${styles.primary}`;
  let submitOnClick: (() => void) | undefined = () => setState('confirming');
  if (state === 'submitting') {
    submitLabel = t('submit.submitting');
    submitIcon = <span className={styles.spinner} aria-hidden="true" />;
    submitOnClick = undefined;
  } else if (state === 'submitted') {
    submitLabel = t('submit.submitted');
    submitClass = `${styles.btn} ${styles.submitted}`;
    submitIcon = <CheckIcon />;
    submitOnClick = undefined;
  } else if (isRetry) {
    submitLabel = t('submit.retryWorkflow');
    submitClass = `${styles.btn} ${styles.primary}`;
    submitOnClick = submit;
  }

  return (
    <div className={styles.footer}>
      {submitBlockedMessage && (
        <Alert severity="warning">{submitBlockedMessage}</Alert>
      )}
      {submitError && (
        <Alert severity="error">{submitError}</Alert>
      )}
      {!submitError && state === 'submitted' && workflowMessages.map((msg, i) => (
        <Alert key={`${i}-${msg}`} severity="info">{msg}</Alert>
      ))}
      <div className={styles.row}>
        <span className={styles.meta}>
          {t('actions.meta', { requests: run.forthApiRequests, seconds: (run.executionTimeMs / 1000).toFixed(1) })}
        </span>

        <div className={styles.buttons}>
          <button type="button" className={`${styles.btn} ${styles.secondary}`} onClick={onStartOver}>
            <HomeIcon />
            <span>{t('submit.startOver')}</span>
          </button>
          <button type="button" className={`${styles.btn} ${styles.secondary}`} onClick={onRerun}>
            <RerunIcon />
            <span>{t('submit.rerun')}</span>
          </button>

          <div className={styles.submitWrap}>
            <button
              type="button"
              className={submitClass}
              onClick={submitOnClick}
              disabled={submitDisabled}
              title={submitTitle}
            >
              {submitIcon}
              <span>{submitLabel}</span>
            </button>

            {state === 'confirming' && (
              <div className={styles.popover} role="dialog" aria-label={t('submit.confirmTitle')}>
                <p>{t('submit.confirmBody')}</p>
                <div className={styles.popoverButtons}>
                  <button type="button" className={styles.cancel} onClick={() => setState('idle')}>
                    {t('submit.cancel')}
                  </button>
                  <button type="button" className={styles.confirm} onClick={submit}>
                    {t('submit.confirm')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
